// A1 <-> {row, col} conversion, range parsing, and relative-reference
// translation for copy/paste. Rows and columns are 1-based throughout, matching
// both Excel and fast-formula-parser's `onCell`/`onRange` contract.

export interface RowCol {
  row: number;
  col: number;
}

export interface CellRefA1 extends RowCol {
  absRow: boolean;
  absCol: boolean;
  sheet?: string;
}

export interface RangeRC {
  r1: number;
  c1: number;
  r2: number;
  c2: number;
  sheet?: string;
}

export const MAX_COL = 16384; // XFD
export const MAX_ROW = 1048576;

/** 1 -> "A", 26 -> "Z", 27 -> "AA". */
export function colToLabel(col: number): string {
  if (col < 1) throw new RangeError(`column out of range: ${col}`);
  let n = col;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** "A" -> 1, "aa" -> 27. Case-insensitive. */
export function labelToCol(label: string): number {
  const s = label.toUpperCase();
  if (!/^[A-Z]{1,3}$/.test(s)) throw new RangeError(`bad column label: ${label}`);
  let n = 0;
  for (let i = 0; i < s.length; i++) n = n * 26 + (s.charCodeAt(i) - 64);
  return n;
}

const CELL_RE = /^(\$?)([A-Za-z]{1,3})(\$?)([1-9][0-9]*)$/;

/** Parse a bare A1 reference (no sheet prefix). Returns null if it isn't one. */
export function parseCellA1(a1: string): CellRefA1 | null {
  const m = CELL_RE.exec(a1.trim());
  if (!m) return null;
  const col = labelToCol(m[2]!);
  const row = Number(m[4]!);
  if (col > MAX_COL || row > MAX_ROW) return null;
  return { row, col, absCol: m[1] === '$', absRow: m[3] === '$' };
}

export function rowColToA1(row: number, col: number, absRow = false, absCol = false): string {
  return `${absCol ? '$' : ''}${colToLabel(col)}${absRow ? '$' : ''}${row}`;
}

/**
 * Parse `A1`, `A1:C9`, `Sheet2!A1:C9`, `'My Sheet'!A1`. A single cell is
 * returned as a 1x1 range so callers have one shape to handle.
 */
export function parseRange(ref: string): RangeRC | null {
  let body = ref.trim();
  let sheet: string | undefined;
  const bang = splitSheetPrefix(body);
  if (bang) {
    sheet = bang.sheet;
    body = bang.rest;
  }
  const parts = body.split(':');
  if (parts.length === 1) {
    const c = parseCellA1(parts[0]!);
    if (!c) return null;
    return { r1: c.row, c1: c.col, r2: c.row, c2: c.col, sheet };
  }
  if (parts.length !== 2) return null;
  const a = parseCellA1(parts[0]!);
  const b = parseCellA1(parts[1]!);
  if (!a || !b) return null;
  return {
    r1: Math.min(a.row, b.row),
    c1: Math.min(a.col, b.col),
    r2: Math.max(a.row, b.row),
    c2: Math.max(a.col, b.col),
    sheet,
  };
}

/** Split a leading `Sheet1!` or `'My Sheet'!` off a reference. */
export function splitSheetPrefix(ref: string): { sheet: string; rest: string } | null {
  if (ref.startsWith("'")) {
    const end = ref.indexOf("'!", 1);
    if (end < 0) return null;
    return { sheet: ref.slice(1, end).replace(/''/g, "'"), rest: ref.slice(end + 2) };
  }
  const bang = ref.indexOf('!');
  if (bang <= 0) return null;
  return { sheet: ref.slice(0, bang), rest: ref.slice(bang + 1) };
}

/** Render a sheet name for use in a formula, quoting only when it must be. */
export function quoteSheetName(name: string): string {
  return /^[A-Za-z_][A-Za-z0-9_.]*$/.test(name) ? name : `'${name.replace(/'/g, "''")}'`;
}

export function rangeToA1(r: RangeRC): string {
  const body =
    r.r1 === r.r2 && r.c1 === r.c2
      ? rowColToA1(r.r1, r.c1)
      : `${rowColToA1(r.r1, r.c1)}:${rowColToA1(r.r2, r.c2)}`;
  return r.sheet ? `${quoteSheetName(r.sheet)}!${body}` : body;
}

export function rangeContains(r: RangeRC, row: number, col: number): boolean {
  return row >= r.r1 && row <= r.r2 && col >= r.c1 && col <= r.c2;
}

export function rangeCellCount(r: RangeRC): number {
  return (r.r2 - r.r1 + 1) * (r.c2 - r.c1 + 1);
}

// ---------------------------------------------------------------------------
// Relative-reference translation (copy/paste, fill handle)
// ---------------------------------------------------------------------------

export interface RefSite {
  row: number;
  col: number;
  absRow: boolean;
  absCol: boolean;
  /**
   * The sheet the reference names, if it names one. A structural change on one
   * sheet has to fix up `Sheet2!A5` written on another — without this the
   * scanner cannot tell which sheet a reference points at, and deleting rows
   * would silently corrupt every cross-sheet formula.
   */
  sheet?: string;
}

/** Where a reference should end up, or null to make it `#REF!`. */
export type Remap = (site: RefSite) => { row: number; col: number } | null;

/**
 * Rewrite every cell reference in a formula through `remap`.
 *
 * Hand-scanned rather than regex-replaced because a blind regex corrupts three
 * things a spreadsheet routinely contains: text inside string literals
 * (`"A1 is empty"`), function names that look like columns (`LOG10(`), and
 * sheet-quoted names. The scanner skips string literals entirely, refuses any
 * identifier immediately followed by `(`, and refuses one preceded by a
 * character that makes it part of a longer name.
 *
 * One scanner, several jobs: copy/paste translates by an offset, deleting rows
 * closes the gap, inserting opens one, and reordering permutes. They differ
 * only in the function passed here — which matters, because a second scanner
 * would be a second place for the `LOG10(` problem to be got wrong.
 */
export function remapFormula(formula: string, remap: Remap): string {
  return walkFormula(formula, (site) => {
    const moved = remap(site);
    if (moved === null || moved.col < 1 || moved.col > MAX_COL || moved.row < 1 || moved.row > MAX_ROW) {
      return '#REF!';
    }
    return rowColToA1(moved.row, moved.col, site.absRow, site.absCol);
  });
}

/**
 * The scanner itself, with the reference handler passed in.
 *
 * One walk, several jobs. `remapFormula` returns replacement text; `scanRefs`
 * records where each reference sits and returns it unchanged. A second copy of
 * this would be a second place for the `LOG10(` problem to be got wrong.
 */
function walkFormula(
  formula: string,
  onRef: (site: RefSite, start: number, end: number) => string
): string {
  let out = '';
  let i = 0;
  const n = formula.length;
  /** The sheet name just consumed, applying to the next reference only. */
  let pendingSheet: string | undefined;

  while (i < n) {
    const ch = formula[i]!;

    // String literal — copy verbatim, including "" escapes.
    if (ch === '"') {
      const start = i;
      i++;
      while (i < n) {
        if (formula[i] === '"') {
          if (formula[i + 1] === '"') i += 2;
          else {
            i++;
            break;
          }
        } else i++;
      }
      out += formula.slice(start, i);
      continue;
    }

    // Single-quoted sheet name — copy verbatim, and remember it if a `!`
    // follows, because the reference after it belongs to that sheet.
    if (ch === "'") {
      const start = i;
      i++;
      while (i < n) {
        if (formula[i] === "'") {
          if (formula[i + 1] === "'") i += 2;
          else {
            i++;
            break;
          }
        } else i++;
      }
      const quoted = formula.slice(start, i);
      out += quoted;
      if (formula[i] === '!') pendingSheet = quoted.slice(1, -1).replace(/''/g, "'");
      continue;
    }

    // Candidate reference: optional $, letters, optional $, digits.
    const m = /^(\$?)([A-Za-z]{1,3})(\$?)([0-9]+)/.exec(formula.slice(i));
    if (m) {
      const whole = m[0]!;
      const after = formula[i + whole.length];
      const before = out.length > 0 ? out[out.length - 1]! : '';
      const isFunctionName = after === '(';
      // `AB1!C2` — Excel forbids sheet names shaped like refs, but if one gets
      // through, the bang tells us this token is a sheet name, not a cell.
      const isSheetName = after === '!';
      // `.` and `!` mean we are mid-identifier or just past a sheet bang that
      // the sheet-name branch already consumed; a letter/digit/_ likewise.
      const glued = /[A-Za-z0-9_.]/.test(before);
      if (isSheetName) {
        // `Sheet2!` written unquoted — remember it for the reference that
        // follows, then fall through and copy it verbatim.
        pendingSheet = whole;
      }
      if (!isFunctionName && !isSheetName && !glued) {
        const absCol = m[1] === '$';
        const absRow = m[3] === '$';
        const col = labelToCol(m[2]!);
        const row = Number(m[4]!);
        const sheet = pendingSheet;
        pendingSheet = undefined;
        out += onRef({ row, col, absRow, absCol, sheet }, i, i + whole.length);
        i += whole.length;
        continue;
      }
    }

    if (ch === '!') {
      // The identifier just copied was a sheet name. Recover it from the tail
      // of `out`, so `Sheet2!A5` reaches the remap knowing its sheet.
      const m2 = /([A-Za-z_][A-Za-z0-9_.]*)$/.exec(out);
      if (m2) pendingSheet = m2[1];
    }
    out += ch;
    i++;
  }
  return out;
}

/**
 * Shift every relative reference in a formula by (dRow, dCol) — copy, paste and
 * fill.
 *
 * `$` anchors are honoured here: `$A$1` never moves, `A$1` moves horizontally
 * only. That is specific to translation. A STRUCTURAL change moves an anchored
 * reference too, because the cell it names has itself moved.
 */
export function translateFormula(formula: string, dRow: number, dCol: number): string {
  if (dRow === 0 && dCol === 0) return formula;
  return remapFormula(formula, ({ row, col, absRow, absCol }) => ({
    row: absRow ? row : row + dRow,
    col: absCol ? col : col + dCol,
  }));
}

// ---------------------------------------------------------------------------
// Highlighting the references in a formula being edited
// ---------------------------------------------------------------------------

export interface RefSpan {
  /** Character offsets into the formula, for colouring the text. */
  start: number;
  end: number;
  /** The rectangle it names — one cell, or the whole of `A1:B3`. */
  r1: number;
  c1: number;
  r2: number;
  c2: number;
  /** The sheet it names, if it names one. */
  sheet?: string;
  /** Stable across the same reference written twice, so both get one colour. */
  key: string;
}

/**
 * Every cell reference in a formula, with where it sits in the text.
 *
 * `A1:B3` comes back as ONE span covering both halves and the colon, because
 * that is what the user drew and what gets outlined on the grid. Two references
 * that name the same thing share a `key`, so writing `A1` twice colours both
 * the same — the colour identifies the cell, not the occurrence.
 */
export function scanRefs(formula: string): RefSpan[] {
  const sites: { site: RefSite; start: number; end: number }[] = [];
  walkFormula(formula, (site, start, end) => {
    sites.push({ site, start, end });
    return formula.slice(start, end);
  });

  const out: RefSpan[] = [];
  for (let i = 0; i < sites.length; i++) {
    const a = sites[i]!;
    const b = sites[i + 1];
    // A range is two references with a colon and nothing else between them.
    const isRange = b !== undefined && b.start === a.end + 1 && formula[a.end] === ':';
    const end = isRange ? b.end : a.end;
    const r1 = Math.min(a.site.row, isRange ? b.site.row : a.site.row);
    const r2 = Math.max(a.site.row, isRange ? b.site.row : a.site.row);
    const c1 = Math.min(a.site.col, isRange ? b.site.col : a.site.col);
    const c2 = Math.max(a.site.col, isRange ? b.site.col : a.site.col);
    const sheet = a.site.sheet;
    out.push({
      start: a.start,
      end,
      r1,
      c1,
      r2,
      c2,
      sheet,
      key: `${(sheet ?? '').toLowerCase()}!${r1},${c1},${r2},${c2}`,
    });
    if (isRange) i++;
  }
  return out;
}

/**
 * The colours a formula's references are drawn in.
 *
 * Excel and Sheets both do this, and it is the only thing that makes a formula
 * with five references readable: the text and the box on the grid share a
 * colour, so the eye pairs them without counting commas. Six hues, cycled —
 * distinct in hue rather than lightness, so they stay apart for a colourblind
 * reader and in a screenshot.
 */
export const REF_COLOURS = ['#3370bd', '#e8562d', '#6fa86a', '#a86fc2', '#e6b840', '#0f9d8a'] as const;

export function refColour(index: number): string {
  return REF_COLOURS[index % REF_COLOURS.length]!;
}

/** Each distinct reference in the order it first appears, with its colour. */
export function colourRefs(formula: string): (RefSpan & { colour: string })[] {
  const order = new Map<string, number>();
  return scanRefs(formula).map((span) => {
    let i = order.get(span.key);
    if (i === undefined) {
      i = order.size;
      order.set(span.key, i);
    }
    return { ...span, colour: refColour(i) };
  });
}

/** Excel's F4 order: both anchored, row only, column only, neither, and back. */
const F4_STATES: readonly { absCol: boolean; absRow: boolean }[] = [
  { absCol: true, absRow: true },
  { absCol: false, absRow: true },
  { absCol: true, absRow: false },
  { absCol: false, absRow: false },
];

function cycleAbsolute(absCol: boolean, absRow: boolean): { absCol: boolean; absRow: boolean } {
  const i = F4_STATES.findIndex((s) => s.absCol === absCol && s.absRow === absRow);
  return F4_STATES[(i + 1) % F4_STATES.length]!;
}

/**
 * F4: cycle the `$` anchors of the reference the caret is on or touching,
 * `$A$1 -> A$1 -> $A1 -> A1 -> $A$1`. A range cycles as one unit — both
 * corners take the SAME new state, from the first corner's current one,
 * same as Excel: `$A$1:$B$2` presses through to `A$1:B$2`, not a mix.
 *
 * Returns null when the caret is not on a reference, so the caller can leave
 * the formula untouched rather than editing nothing into nothing.
 */
export function toggleReferenceAt(text: string, caret: number): { text: string; caret: number } | null {
  const span = scanRefs(text).find((s) => caret >= s.start && caret <= s.end);
  if (!span) return null;
  const ref = text.slice(span.start, span.end);

  const prefixed = splitSheetPrefix(ref);
  const prefix = prefixed ? ref.slice(0, ref.length - prefixed.rest.length) : '';
  const rest = prefixed ? prefixed.rest : ref;

  const colon = rest.indexOf(':');
  const leftText = colon < 0 ? rest : rest.slice(0, colon);
  const rightText = colon < 0 ? null : rest.slice(colon + 1);

  const left = parseCellA1(leftText);
  const right = rightText === null ? null : parseCellA1(rightText);
  if (!left || (rightText !== null && !right)) return null;

  const next = cycleAbsolute(left.absCol, left.absRow);
  const newLeft = rowColToA1(left.row, left.col, next.absRow, next.absCol);
  const newRest = right ? `${newLeft}:${rowColToA1(right.row, right.col, next.absRow, next.absCol)}` : newLeft;
  const newRef = prefix + newRest;

  return {
    text: text.slice(0, span.start) + newRef + text.slice(span.end),
    caret: span.start + newRef.length,
  };
}
